<template>
  <Modal
    ref="needChangePasswordModalRef"
    positiveActionText="Giriş Yap"
    @onPositiveActionClick="approvePasswordChange"
    :showIcon="false"
    :modalStyle="{ width: '460px' }"
    :withCloser="false"
    :outsideClickDisabled="outsideClickDisabled"
  >
    <template v-slot:body>
      <div class="modal-body top">
        <h3 class="modal-title">Şifrenizi Değiştirin</h3>
        <div class="info-box-wrapper">
          <div class="info-box">
            <img
              width="20px"
              height="20px"
              src="@/assets/images/icons/info-circle.svg"
            />
            <div class="info-box-text">
              <p>
                Giriş yapmak için, telefonunuza gönderdiğimiz şifreyi
                <span>yeni bir şifre belirleyerek</span> değiştirin. Lütfen
                belirlediğiniz şifreyi kimseyle paylaşmayın.
              </p>
            </div>
          </div>
        </div>
        <Input
          type="password"
          id="currentPassword"
          label="Mevcut Şifre"
          size="lg"
          v-model:value="password"
          v-model:isError="isPasswordError"
          :helperText="passwordErrorText"
          :showPasswordToggle="true"
        />
        <div class="new-password-container">
          <Input
            type="password"
            id="newPassword"
            label="Yeni Şifre"
            size="lg"
            v-model:value="newPassword"
            v-model:isError="isNewPasswordError"
            :showPasswordToggle="true"
          />
          <div class="validation-list">
            <p
              v-for="(rule, key) in passwordRules"
              :key="key"
              :class="{
                valid: rule.valid,
                invalid: !rule.valid && isPasswordStarted,
              }"
            >
              <span>•</span> {{ rule.text }}
            </p>
          </div>
        </div>

        <Input
          type="password"
          id="newPasswordConfirm"
          label="Yeni Şifre Tekrar"
          size="lg"
          v-model:value="newPasswordConfirm"
          v-model:isError="isNewPasswordConfirmError"
          :helperText="newPasswordConfirmErrorText"
          :showPasswordToggle="true"
        />
      </div>
    </template>
  </Modal>
</template>

<script setup>
import { ref, computed, watch } from "vue";
import { Modal, Input } from "@n11/so-ui-components";
import { useStore } from "vuex";
import { ToastMessageTypes } from "@/utils/enum/toastMessageTypes";

const store = useStore();
const needChangePasswordModalRef = ref(null);
const outsideClickDisabled = ref(true);

const password = ref("");
const newPassword = ref("");
const newPasswordConfirm = ref("");

const isPasswordError = ref(false);
const isNewPasswordError = ref(false);
const isNewPasswordConfirmError = ref(false);

const passwordErrorText = ref("");
const newPasswordErrorText = ref("");
const newPasswordConfirmErrorText = ref("");

const isPasswordStarted = computed(() => newPassword.value.length > 0);

const passwordRules = computed(() => [
  {
    text: "8-15 karakter",
    valid: newPassword.value.length >= 8 && newPassword.value.length <= 15,
  },
  { text: "En az 1 rakam", valid: /\d/.test(newPassword.value) },
  {
    text: "En az 1 büyük, 1 küçük harf",
    valid: /[a-z]/.test(newPassword.value) && /[A-Z]/.test(newPassword.value),
  },
  {
    text: "En az 1 sembol",
    valid: /[!@#$%^&*()_+.:;,]/.test(newPassword.value),
  },
]);

const validatePasswords = () => {
  isPasswordError.value = false;
  isNewPasswordError.value = false;
  isNewPasswordConfirmError.value = false;

  passwordErrorText.value = "";
  newPasswordErrorText.value = "";
  newPasswordConfirmErrorText.value = "";

  if (newPassword.value === password.value) {
    newPasswordErrorText.value = "Yeni şifre ile mevcut şifre aynı olamaz.";
    isNewPasswordError.value = true;
  }

  if (newPassword.value !== newPasswordConfirm.value) {
    newPasswordConfirmErrorText.value = "Yeni şifreler eşleşmiyor.";
    isNewPasswordConfirmError.value = true;
  }
  return !(isNewPasswordError.value || isNewPasswordConfirmError.value);
};

const closeModal = () => {
  needChangePasswordModalRef.value.hiddenModal();
};

const approvePasswordChange = async () => {
  if (validatePasswords()) {
    try {
      outsideClickDisabled.value = false;
      const res = await store.dispatch("accounts/changePasswordSubAccount", {
        currentPassword: password.value,
        newPassword: newPassword.value,
      });
      store.commit("common/SET_TOASTER_DATA", {
        type: ToastMessageTypes.SUCCESS,
        description: res.message,
      });
      closeModal();
    } catch (error) {
      outsideClickDisabled.value = true;
      store.commit("common/SET_TOASTER_DATA", {
        type: ToastMessageTypes.ERROR,
        title: ToastMessageTypes.ERROR_TITLE,
        description: error.message || "Bir hata oluştu",
      });
    }
  }
};

const openModal = () => {
  needChangePasswordModalRef.value.openModal();
};

//modalin local ortamda acilmasi icin ilgili alt hesapla giris yaptiktan sonra cookie den test_acctype-id degeri localhost olarak degisitirilmeli.

watch([newPassword, newPasswordConfirm], validatePasswords);

defineExpose({ openModal });
</script>

<style lang="scss" scoped>
@import "@/assets/styles/variables.scss";
@import "@/assets/styles/utilities.scss";
.modal-body {
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 8px;
  &.top {
    margin-bottom: 14px;
  }
  &.buttom {
    margin-bottom: 4px;
  }
  .modal-title {
    text-align: start;
    // position: absolute;
    top: 14px;
    font-size: 20px;
    font-weight: 700;
    line-height: 26px;
    letter-spacing: -0.2px;
  }
  .modal-text {
    text-align: start;
    font-size: 16px;
    font-style: normal;
    font-weight: 400;
    line-height: 24px;
    letter-spacing: -0.05px;
  }
  .subTitle {
    font-size: 12px;
    font-style: normal;
    font-weight: 400;
    line-height: 16px;
  }
}

.info-box-wrapper {
  .info-box {
    display: flex;
    background-color: $colorB10;
    padding: 16px;
    gap: 12px;
    border-radius: 6px;
    align-items: center;
    .info-box-text {
      color: $colorN80;
      font-size: 13px;
      font-style: normal;
      font-weight: 400;
      line-height: 18px;
      letter-spacing: -0.05px;
      text-align: left;
    }
  }
}

:deep(.input-wrapper.error .input-helperText) {
  color: $colorE50;
  text-align: start;
}

.new-password-container {
  width: 100%;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
}

.validation-list {
  list-style: none;
  padding: 0;
  margin-top: 8px;
  display: flex;
  justify-content: space-between;
  gap: 8px;
  width: 100%;

  p {
    font-size: 10px;
    line-height: 12px;
    color: $colorN60;
    display: flex;
    align-items: center;
    gap: 4px;
    span {
      font-size: 19px;
    }
  }

  .valid {
    color: green;
  }
  .invalid {
    color: red;
  }
}
</style>
